/**
 * Navigation model for the unified sidebar.
 *
 * Built from the two existing menu endpoints — see `nav-tree.builder.ts`:
 *   GET /api/menu/modules   -> ModuleSelectorApiDto[]  (roots only, carries isEnabled)
 *   GET /api/menu           -> MenuItemApiDto[]        (full forest, carries permissions)
 *
 * These are deliberately separate from `menu.model.ts`'s MenuItem/MenuModule, which describe
 * the *wire* shape. This file describes what the sidebar renders.
 */

/**
 * Derived from tree SHAPE, never from the API's `featureType`.
 *
 * `featureType` is unreliable across environments: `db/bootstrap/common/*.sql` (what the deploy
 * actually runs) never inserts it, so it is NULL in any CI-bootstrapped database, while the
 * historical hand-run `BookingHub.API/SQLScrpt/1.sql` does set it. Shape is always true.
 */
export type NavKind = 'module' | 'section' | 'page';

export interface NavPerms {
  view: boolean;
  add: boolean;
  edit: boolean;
  remove: boolean;
  exportData: boolean;
  importData: boolean;
  /** Precomputed OR of the six — the visibility rule the API and the old drawers both used. */
  any: boolean;
}

export interface NavNode {
  id: number;                 // featureId
  parentId: number | null;
  kind: NavKind;
  label: string;              // aliasName ?? featureName
  icon: string;               // 'bi-*', with a per-kind fallback
  /**
   * null means NOT navigable. Never the empty string — `[routerLink]=""` silently
   * self-navigates, which is the bug the old page-drawer shipped with.
   */
  path: string | null;
  order: number;
  /** True when the label is long enough to truncate at 280px; drives a `title` attribute. */
  needsTitle: boolean;
  perms: NavPerms;
  children: NavNode[];
}

export interface NavModule extends NavNode {
  kind: 'module';
  /**
   * De-inverted from `ModuleSelectorApiDto.isEnabled`.
   *
   * THE API FIELD IS INVERTED: `isEnabled === true` means the module is LOCKED / "coming soon".
   * This is the single inversion point in the app — everything downstream reads `isLocked`.
   */
  isLocked: boolean;
  /** First accessible leaf, precomputed. Where a module click navigates to. */
  defaultPath: string | null;
}

/** One row of the flattened path index used for longest-prefix active-node matching. */
export interface NavPathIndexEntry {
  segs: string[];             // lowercased path segments
  nodeId: number;
  moduleId: number;
  sectionId: number | null;
  kind: NavKind;
  order: number;
}

export interface NavTree {
  /** Unlocked modules — these fill the icon rail and the panel. */
  live: NavModule[];
  /** Locked modules — panel-only, inside the "Coming soon" group. Never in the rail. */
  locked: NavModule[];
  byId: ReadonlyMap<number, NavNode>;
  /** Sorted by segment count DESC so the first match is the longest. */
  index: NavPathIndexEntry[];
  /**
   * Every path the navigation system manages, for ANY user (from GET /api/menu/paths).
   *
   * Lets the route guard separate "this page exists in the menu but I was filtered out of
   * it" (deny) from "this route has no menu row at all" (allow). The permission-filtered
   * tree cannot express that, because a denied node is simply absent from it.
   */
  menuManagedPaths: ReadonlySet<string>;
}

export interface ActiveNav {
  moduleId: number | null;
  sectionId: number | null;
  pageId: number | null;
}

export const EMPTY_ACTIVE: ActiveNav = { moduleId: null, sectionId: null, pageId: null };

export const EMPTY_NAV_TREE: NavTree = {
  live: [],
  locked: [],
  byId: new Map<number, NavNode>(),
  index: [],
  menuManagedPaths: new Set<string>(),
};
